/**
 * SchedulePredictOps — AI-driven schedule optimization suite
 * 5 sub-tabs: Monte Carlo · Delay Prediction · NL Query · Quality Index · Weather Impact
 */
import React, { useState, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { toast } from 'sonner';
import {
  Dice5, AlertTriangle, MessageCircle, ShieldCheck, CloudRain, Loader2, RefreshCw,
  Sparkles, ArrowRight,
} from 'lucide-react';
import api from '@/lib/api';

const surfacePanel = 'bg-white dark:bg-gradient-to-br dark:from-[#0c1222] dark:to-[#0a0f1e] border border-slate-200 dark:border-slate-700/50 rounded-xl';
const subPanel     = 'bg-slate-50 dark:bg-[#0a0f1e] border border-slate-200 dark:border-slate-700/40 rounded-lg';
const headTxt      = 'text-slate-800 dark:text-white';
const mutedTxt     = 'text-slate-500 dark:text-slate-400';
const bodyTxt      = 'text-slate-700 dark:text-slate-300';

// ═══════════════════════════════════════════════════
// 1. MONTE CARLO
// ═══════════════════════════════════════════════════
function MonteCarloPanel({ scheduleId }) {
  const [iterations, setIterations] = useState(1000);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingP, setSavingP] = useState(null); // '50' | '80' | '90' | null

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.post(`/scheduling/ai-ops/schedules/${scheduleId}/monte-carlo?iterations=${iterations}`);
      setData(r.data);
    } catch (e) { toast.error('Monte Carlo failed'); }
    finally { setLoading(false); }
  }, [scheduleId, iterations]);

  const saveAsScenario = async (percentile) => {
    if (!data) return;
    const pDays = data[`p${percentile}_days`];
    setSavingP(String(percentile));
    try {
      const r = await api.post(`/scheduling/ai-ops/schedules/${scheduleId}/mc-to-scenario`, {
        percentile,
        p_days: pDays,
        planned_days: data.planned_duration_days,
      });
      toast.success(`P${percentile} saved as scenario "${r.data.label}" — ${r.data.tasks_stretched} tasks stretched by ${((r.data.stretch_factor - 1) * 100).toFixed(1)}%`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save scenario');
    } finally { setSavingP(null); }
  };

  const histData = data?.histogram ? data.histogram.labels.map((l, i) => ({ days: l, count: data.histogram.counts[i] })) : [];

  return (
    <div className={`${surfacePanel} p-4`} data-testid="mc-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className={`text-lg font-semibold ${headTxt} flex items-center gap-2`}>
            <Dice5 className="w-4 h-4 text-indigo-500" /> Monte Carlo Simulation
          </h3>
          <p className={`text-xs ${mutedTxt}`}>Uses historical duration variance from past projects to run N stochastic trials on the schedule.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className={`text-xs ${bodyTxt}`}>Iterations</Label>
          <Input type="number" min={100} max={5000} step={100} value={iterations} onChange={(e) => setIterations(parseInt(e.target.value) || 1000)} className="w-24 h-8" data-testid="mc-iterations" />
          <Button size="sm" onClick={run} disabled={loading} className="bg-indigo-500 hover:bg-indigo-600 text-white h-8" data-testid="mc-run">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            {[
              { label: 'Planned',  days: data.planned_duration_days, date: data.planned_finish?.slice(0,10), color: 'sky',      p: null },
              { label: 'P50',      days: data.p50_days, date: data.p50_date?.slice(0,10), color: 'emerald', p: 50 },
              { label: 'P80',      days: data.p80_days, date: data.p80_date?.slice(0,10), color: 'amber',   p: 80 },
              { label: 'P90',      days: data.p90_days, date: data.p90_date?.slice(0,10), color: 'red',     p: 90 },
            ].map(k => (
              <div key={k.label} className={`${subPanel} p-3 border-l-4 border-l-${k.color}-500 relative`}>
                <div className={`text-[10px] uppercase tracking-wider text-${k.color}-600 dark:text-${k.color}-400`}>{k.label}</div>
                <div className={`text-lg font-bold text-${k.color}-600 dark:text-${k.color}-300 mt-1`}>{k.days}d</div>
                <div className={`text-[11px] ${mutedTxt}`}>{k.date}</div>
                {k.p && (
                  <Button
                    size="sm" variant="ghost" className="absolute top-1 right-1 h-6 px-2 text-[10px]"
                    onClick={() => saveAsScenario(k.p)} disabled={savingP === String(k.p)}
                    data-testid={`mc-save-p${k.p}`}
                  >
                    {savingP === String(k.p) ? <Loader2 className="w-3 h-3 animate-spin" /> : <>Save as Scenario</>}
                  </Button>
                )}
              </div>
            ))}
          </div>

          <div className={`${subPanel} p-3 mb-3 text-sm ${bodyTxt}`}>{data.narrative}</div>

          <div className={`${subPanel} p-3 mb-3`}>
            <div className={`text-[10px] uppercase tracking-wider ${mutedTxt} mb-2`}>Completion Duration Histogram (days)</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={histData}>
                <XAxis dataKey="days" stroke="#94a3b8" tick={{ fontSize: 10 }} />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ background: '#0a0f1e', border: '1px solid #334155', color: '#fff', fontSize: 12 }} />
                <ReferenceLine x={data.planned_duration_days} stroke="#0ea5e9" strokeDasharray="4 4" label={{ value: 'Plan', fill: '#0ea5e9', fontSize: 10 }} />
                <ReferenceLine x={data.p50_days} stroke="#10b981" strokeDasharray="4 4" label={{ value: 'P50', fill: '#10b981', fontSize: 10 }} />
                <ReferenceLine x={data.p80_days} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: 'P80', fill: '#f59e0b', fontSize: 10 }} />
                <ReferenceLine x={data.p90_days} stroke="#ef4444" strokeDasharray="4 4" label={{ value: 'P90', fill: '#ef4444', fontSize: 10 }} />
                <Bar dataKey="count" fill="#6366f1" opacity={0.7} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {(data.sensitivity || []).length > 0 && (
            <div className={`${subPanel} overflow-hidden`}>
              <div className={`px-3 py-2 text-[10px] uppercase tracking-wider ${mutedTxt} border-b border-slate-200 dark:border-slate-700/40`}>
                Top 10 Sensitivity — Critical Tasks Most Frequently Causing Slippage
              </div>
              <div className="max-h-[300px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 dark:bg-slate-800/40 sticky top-0"><tr>
                    <th className={`p-2 text-left ${mutedTxt}`}>WBS</th>
                    <th className={`p-2 text-left ${mutedTxt}`}>Task</th>
                    <th className={`p-2 text-right ${mutedTxt}`}>Delay Frequency</th>
                  </tr></thead>
                  <tbody>
                    {data.sensitivity.map(s => (
                      <tr key={s.task_id} className="border-t border-slate-200 dark:border-slate-700/30">
                        <td className={`p-2 ${mutedTxt} font-mono text-[10px]`}>{s.wbs_code}</td>
                        <td className={`p-2 ${headTxt}`}>{s.name}</td>
                        <td className={`p-2 text-right text-red-500`}>{s.delay_frequency_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {!data && !loading && (
        <div className={`${subPanel} p-6 text-center text-sm ${mutedTxt}`}>
          Click Run to execute {iterations} Monte-Carlo trials. Uses PERT-triangular sampling on task durations.
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// 2. PREDICTIVE DELAY DETECTION
// ═══════════════════════════════════════════════════
function PredictDelaysPanel({ scheduleId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.post(`/scheduling/ai-ops/schedules/${scheduleId}/predict-delays`, {});
      setData(r.data);
    } catch (e) { toast.error('Prediction failed'); }
    finally { setLoading(false); }
  }, [scheduleId]);

  const riskColor = (score) => score >= 70 ? 'red' : score >= 40 ? 'amber' : 'sky';

  return (
    <div className={`${surfacePanel} p-4`} data-testid="predict-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className={`text-lg font-semibold ${headTxt} flex items-center gap-2`}>
            <AlertTriangle className="w-4 h-4 text-red-500" /> Predictive Delay Detection
          </h3>
          <p className={`text-xs ${mutedTxt}`}>Progress + resource + weather signals + Cognara-powered corrective actions</p>
        </div>
        <Button size="sm" onClick={run} disabled={loading} className="bg-red-500 hover:bg-red-600 text-white h-8" data-testid="predict-run">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
          Predict Now
        </Button>
      </div>

      {data && (
        <>
          <div className={`${subPanel} p-3 mb-3`}>
            <div className={`text-[10px] uppercase tracking-wider ${mutedTxt} mb-1`}>AI Summary</div>
            <div className={`text-sm ${bodyTxt}`}>{data.ai_summary || 'AI layer inactive — showing deterministic at-risk list only.'}</div>
            <div className={`text-[11px] ${mutedTxt} mt-1`}>Total at-risk tasks: <span className={`font-bold text-red-600 dark:text-red-400`}>{data.total_at_risk}</span></div>
          </div>

          {(data.predictions || []).length > 0 && (
            <div className={`${subPanel} overflow-hidden mb-3`}>
              <div className={`px-3 py-2 text-[10px] uppercase tracking-wider ${mutedTxt} border-b border-slate-200 dark:border-slate-700/40`}>Cognara Predictions & Corrective Actions</div>
              <div className="max-h-[280px] overflow-auto p-3 space-y-3">
                {data.predictions.map((p, i) => (
                  <div key={i} className={`${subPanel} p-2`}>
                    <div className={`text-xs font-semibold ${headTxt}`}>{p.task_id}</div>
                    <div className={`text-xs ${bodyTxt} mt-1`}>{p.prediction}</div>
                    <div className={`text-[10px] ${mutedTxt} mt-1`}>Confidence: {p.confidence}</div>
                    {(p.corrective_actions || []).map((a, j) => (
                      <div key={j} className={`text-[11px] ${bodyTxt} mt-0.5`}><ArrowRight className="w-3 h-3 inline text-emerald-500" /> {a}</div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={`${subPanel} overflow-hidden`}>
            <div className={`px-3 py-2 text-[10px] uppercase tracking-wider ${mutedTxt} border-b border-slate-200 dark:border-slate-700/40`}>Top At-Risk Tasks</div>
            <div className="max-h-[380px] overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 dark:bg-slate-800/40 sticky top-0"><tr>
                  <th className={`p-2 text-left ${mutedTxt}`}>WBS</th>
                  <th className={`p-2 text-left ${mutedTxt}`}>Task</th>
                  <th className={`p-2 text-right ${mutedTxt}`}>Planned %</th>
                  <th className={`p-2 text-right ${mutedTxt}`}>Actual %</th>
                  <th className={`p-2 text-right ${mutedTxt}`}>Variance</th>
                  <th className={`p-2 text-right ${mutedTxt}`}>Risk</th>
                </tr></thead>
                <tbody>
                  {(data.at_risk_top || []).map(t => (
                    <tr key={t.task_id} className="border-t border-slate-200 dark:border-slate-700/30">
                      <td className={`p-2 ${mutedTxt} font-mono text-[10px]`}>{t.wbs_code}</td>
                      <td className={`p-2 ${headTxt}`}>{t.name}</td>
                      <td className={`p-2 text-right ${bodyTxt}`}>{t.planned_pct}%</td>
                      <td className={`p-2 text-right ${bodyTxt}`}>{t.actual_pct}%</td>
                      <td className={`p-2 text-right text-amber-600 dark:text-amber-400`}>{t.variance_pct}%</td>
                      <td className={`p-2 text-right`}><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold bg-${riskColor(t.risk_score)}-100 dark:bg-${riskColor(t.risk_score)}-500/20 text-${riskColor(t.risk_score)}-700 dark:text-${riskColor(t.risk_score)}-300`}>{t.risk_score}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!data && !loading && <div className={`${subPanel} p-6 text-center text-sm ${mutedTxt}`}>Click Predict Now to scan the schedule for slippage risk and receive Cognara corrective actions.</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// 3. NATURAL LANGUAGE QUERY
// ═══════════════════════════════════════════════════
function NLQueryPanel({ scheduleId }) {
  const [query, setQuery] = useState('What happens if steel erection is delayed by 3 weeks?');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    if (!scheduleId) return;
    try {
      const r = await api.get(`/scheduling/ai-ops/schedules/${scheduleId}/nl-queries`);
      setHistory(r.data || []);
    } catch (_) { /* silent */ }
  }, [scheduleId]);

  React.useEffect(() => { loadHistory(); }, [loadHistory]);

  const ask = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const r = await api.post(`/scheduling/ai-ops/schedules/${scheduleId}/nl-query`, { query });
      setData(r.data);
      // Persist to history (fire-and-forget)
      try {
        await api.post(`/scheduling/ai-ops/schedules/${scheduleId}/nl-queries`, { query, result: r.data });
        loadHistory();
      } catch (_) { /* silent */ }
    } catch (e) { toast.error('Query failed'); }
    finally { setLoading(false); }
  };

  const rerun = (q) => {
    setQuery(q);
    // trigger ask after state settles
    setTimeout(() => { document.querySelector('[data-testid="nl-run"]')?.click(); }, 100);
  };

  const deleteHistoryItem = async (id) => {
    try {
      await api.delete(`/scheduling/ai-ops/schedules/${scheduleId}/nl-queries/${encodeURIComponent(id)}`);
      loadHistory();
    } catch (_) { /* silent */ }
  };

  return (
    <div className={`${surfacePanel} p-4`} data-testid="nl-panel">
      <div className="mb-3">
        <h3 className={`text-lg font-semibold ${headTxt} flex items-center gap-2`}>
          <MessageCircle className="w-4 h-4 text-fuchsia-500" /> Natural-Language Schedule Query
        </h3>
        <p className={`text-xs ${mutedTxt}`}>Ask a plain-English question. The AI extracts date-shifts and simulates the impact instantly.</p>
      </div>

      <div className="flex items-start gap-2 mb-3">
        <Textarea
          value={query} onChange={(e) => setQuery(e.target.value)}
          className="flex-1 h-20 text-sm"
          placeholder="e.g. What if concrete pours are delayed by 5 days? Or: how does adding 2 weeks to steel affect the finish?"
          data-testid="nl-input"
        />
        <Button className="bg-fuchsia-500 hover:bg-fuchsia-600 text-white h-20 px-4" onClick={ask} disabled={loading || !query.trim()} data-testid="nl-run">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" /> Ask</>}
        </Button>
      </div>

      {history.length > 0 && (
        <div className={`${subPanel} p-2 mb-3`} data-testid="nl-history">
          <div className={`text-[10px] uppercase tracking-wider ${mutedTxt} mb-1.5`}>Recent Queries ({history.length}) — click to re-run</div>
          <div className="flex flex-wrap gap-1.5">
            {history.map(h => (
              <div key={h.id} className="group inline-flex items-center gap-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full pl-2 pr-1 py-0.5 text-[11px] hover:bg-fuchsia-50 dark:hover:bg-fuchsia-500/10">
                <button
                  onClick={() => rerun(h.query)}
                  className={`${bodyTxt} max-w-[280px] truncate`}
                  title={`${h.query}${h.slip_days != null ? ` — slip ${h.slip_days}d, ${h.impacted || 0} tasks` : ''}`}
                  data-testid={`nl-history-item-${h.id}`}
                >
                  {h.query.length > 60 ? h.query.slice(0, 60) + '…' : h.query}
                  {h.slip_days != null && <span className={`ml-1 text-[10px] ${h.slip_days > 0 ? 'text-red-500' : 'text-emerald-500'}`}>({h.slip_days > 0 ? '+' : ''}{h.slip_days}d)</span>}
                </button>
                <button
                  onClick={() => deleteHistoryItem(h.id)}
                  className={`opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 px-1`}
                  title="Remove"
                >×</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {data && data.needs_clarification && (
        <div className={`${subPanel} p-3 border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 text-xs ${bodyTxt}`}>
          <div className="font-semibold text-amber-700 dark:text-amber-300 mb-1">Needs Clarification</div>
          {data.clarification}
        </div>
      )}

      {data && !data.needs_clarification && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div className={`${subPanel} p-3`}>
              <div className={`text-[10px] uppercase tracking-wider ${mutedTxt}`}>Original Finish</div>
              <div className={`text-base font-bold ${headTxt} mt-1`}>{data.original_finish?.slice(0,10)}</div>
            </div>
            <div className={`${subPanel} p-3 border-l-4 border-l-amber-500`}>
              <div className={`text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400`}>Projected Finish</div>
              <div className={`text-base font-bold text-amber-600 dark:text-amber-300 mt-1`} data-testid="nl-proj-finish">{data.projected_finish?.slice(0,10)}</div>
            </div>
            <div className={`${subPanel} p-3 border-l-4 border-l-red-500`}>
              <div className={`text-[10px] uppercase tracking-wider text-red-600 dark:text-red-400`}>Slip</div>
              <div className={`text-base font-bold text-red-600 dark:text-red-300 mt-1`}>{data.slip_days > 0 ? '+' : ''}{data.slip_days}d</div>
            </div>
            <div className={`${subPanel} p-3`}>
              <div className={`text-[10px] uppercase tracking-wider ${mutedTxt}`}>Impacted Tasks</div>
              <div className={`text-base font-bold ${headTxt} mt-1`}>{data.downstream_impacted_count}</div>
            </div>
          </div>

          {data.intent && <div className={`${subPanel} p-2 mb-3 text-xs ${mutedTxt}`}>Intent: <span className={bodyTxt}>{data.intent}</span></div>}
          {data.narrative && <div className={`${subPanel} p-3 mb-3 text-sm ${bodyTxt}`}>{data.narrative}</div>}

          {(data.modified_tasks || []).length > 0 && (
            <div className={`${subPanel} overflow-hidden`}>
              <div className={`px-3 py-2 text-[10px] uppercase tracking-wider ${mutedTxt} border-b border-slate-200 dark:border-slate-700/40`}>Modified Tasks (showing up to 50)</div>
              <div className="max-h-[280px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 dark:bg-slate-800/40 sticky top-0"><tr>
                    <th className={`p-2 text-left ${mutedTxt}`}>WBS</th>
                    <th className={`p-2 text-left ${mutedTxt}`}>Task</th>
                    <th className={`p-2 text-left ${mutedTxt}`}>Old Finish</th>
                    <th className={`p-2 text-left ${mutedTxt}`}>New Finish</th>
                    <th className={`p-2 text-right ${mutedTxt}`}>Delay</th>
                  </tr></thead>
                  <tbody>
                    {data.modified_tasks.map(t => (
                      <tr key={t.task_id} className="border-t border-slate-200 dark:border-slate-700/30">
                        <td className={`p-2 ${mutedTxt} font-mono text-[10px]`}>{t.wbs_code}</td>
                        <td className={`p-2 ${headTxt}`}>{t.name}</td>
                        <td className={`p-2 ${bodyTxt}`}>{t.old_finish?.slice(0,10)}</td>
                        <td className={`p-2 text-emerald-600 dark:text-emerald-400`}>{t.new_finish?.slice(0,10)}</td>
                        <td className={`p-2 text-right text-amber-600 dark:text-amber-400`}>+{t.delay_days}d</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// 4. SCHEDULE QUALITY INDEX
// ═══════════════════════════════════════════════════
function QualityIndexPanel({ scheduleId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/scheduling/ai-ops/schedules/${scheduleId}/quality-index`);
      setData(r.data);
    } catch (e) { toast.error('SQI failed'); }
    finally { setLoading(false); }
  }, [scheduleId]);

  React.useEffect(() => { load(); }, [load]);

  const gradeColor = (g) => g === 'A' ? 'emerald' : g === 'B' ? 'sky' : g === 'C' ? 'amber' : 'red';
  const sevColor = (s) => s === 'HIGH' ? 'red' : s === 'MEDIUM' ? 'amber' : 'emerald';

  return (
    <div className={`${surfacePanel} p-4`} data-testid="sqi-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className={`text-lg font-semibold ${headTxt} flex items-center gap-2`}>
            <ShieldCheck className="w-4 h-4 text-emerald-500" /> Schedule Quality Index
          </h3>
          <p className={`text-xs ${mutedTxt}`}>DCMA-inspired 7-point quality audit — open ends, negative float, missing logic, etc.</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="sqi-refresh">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </Button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className={`${subPanel} p-3 text-center border-l-4 border-l-${gradeColor(data.grade)}-500`}>
              <div className={`text-[10px] uppercase tracking-wider ${mutedTxt}`}>Score</div>
              <div className={`text-4xl font-bold text-${gradeColor(data.grade)}-600 dark:text-${gradeColor(data.grade)}-300 mt-1`} data-testid="sqi-score">{data.score}</div>
              <div className={`text-lg font-semibold text-${gradeColor(data.grade)}-600 dark:text-${gradeColor(data.grade)}-300`}>Grade {data.grade}</div>
            </div>
            <div className={`${subPanel} p-3 text-center`}>
              <div className={`text-[10px] uppercase tracking-wider text-red-600 dark:text-red-400`}>Critical Issues</div>
              <div className={`text-3xl font-bold text-red-600 dark:text-red-300 mt-1`}>{data.critical_issues_count}</div>
            </div>
            <div className={`${subPanel} p-3 text-center`}>
              <div className={`text-[10px] uppercase tracking-wider ${mutedTxt}`}>Tasks Audited</div>
              <div className={`text-3xl font-bold ${headTxt} mt-1`}>{data.task_count}</div>
            </div>
          </div>

          <div className={`${subPanel} p-3 mb-3 text-sm ${bodyTxt}`}>{data.narrative}</div>

          <div className="space-y-2">
            {(data.checks || []).map(c => (
              <div key={c.code} className={`${subPanel} p-3 border-l-4 border-l-${sevColor(c.severity)}-500`}>
                <div className="flex items-center justify-between">
                  <div className={`text-sm font-semibold ${headTxt}`}>{c.label}
                    <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full bg-${sevColor(c.severity)}-100 dark:bg-${sevColor(c.severity)}-500/20 text-${sevColor(c.severity)}-700 dark:text-${sevColor(c.severity)}-300 font-bold`}>{c.severity}</span>
                  </div>
                  <div className={`text-xs ${mutedTxt}`}>{c.ratio_pct}% (target ≤ {c.target_pct}%)</div>
                </div>
                <div className={`text-xs ${bodyTxt} mt-1`}>{c.message}</div>
                <div className={`text-[11px] ${mutedTxt} mt-1`}><ArrowRight className="w-3 h-3 inline text-emerald-500" /> {c.recommendation}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// 5. WEATHER IMPACT
// ═══════════════════════════════════════════════════
function WeatherImpactPanel({ scheduleId }) {
  const [horizon, setHorizon] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/scheduling/ai-ops/schedules/${scheduleId}/weather-impact`, { params: { horizon_days: horizon } });
      setData(r.data);
    } catch (e) { toast.error('Weather analysis failed'); }
    finally { setLoading(false); }
  }, [scheduleId, horizon]);

  React.useEffect(() => { load(); }, [load]);

  return (
    <div className={`${surfacePanel} p-4`} data-testid="weather-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className={`text-lg font-semibold ${headTxt} flex items-center gap-2`}>
            <CloudRain className="w-4 h-4 text-sky-500" /> Weather-Integrated Scheduling
          </h3>
          <p className={`text-xs ${mutedTxt}`}>Flags weather-sensitive activities (concrete pours, roofing, earthwork, painting) against upcoming forecast.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className={`text-xs ${bodyTxt}`}>Horizon</Label>
          <Input type="number" min={7} max={90} step={7} value={horizon} onChange={(e) => setHorizon(parseInt(e.target.value) || 30)} className="w-16 h-8" />
          <span className={`text-xs ${mutedTxt}`}>days</span>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="weather-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className={`${subPanel} p-3 text-center`}>
              <div className={`text-[10px] uppercase tracking-wider text-sky-600 dark:text-sky-400`}>Weather-Sensitive Tasks</div>
              <div className={`text-3xl font-bold text-sky-600 dark:text-sky-300 mt-1`} data-testid="weather-impacted">{data.impacted_count}</div>
            </div>
            <div className={`${subPanel} p-3 text-center`}>
              <div className={`text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400`}>Total Recommended Delay</div>
              <div className={`text-3xl font-bold text-amber-600 dark:text-amber-300 mt-1`}>{data.total_recommended_delay_days}d</div>
            </div>
            <div className={`${subPanel} p-3 text-center`}>
              <div className={`text-[10px] uppercase tracking-wider ${mutedTxt}`}>Forecast Window</div>
              <div className={`text-3xl font-bold ${headTxt} mt-1`}>{data.horizon_days}d</div>
            </div>
          </div>

          <div className={`${subPanel} p-3 mb-3 text-sm ${bodyTxt}`}>{data.narrative}</div>

          {(data.forecast || []).length > 0 && (
            <div className={`${subPanel} p-3 mb-3`}>
              <div className={`text-[10px] uppercase tracking-wider ${mutedTxt} mb-2`}>Next 30-Day Forecast (Rain mm / Wind km/h)</div>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={data.forecast.slice(0, 30).map(f => ({ ...f, date: f.date.slice(5) }))}>
                  <XAxis dataKey="date" stroke="#94a3b8" tick={{ fontSize: 9 }} />
                  <YAxis stroke="#94a3b8" tick={{ fontSize: 9 }} />
                  <Tooltip contentStyle={{ background: '#0a0f1e', border: '1px solid #334155', color: '#fff', fontSize: 11 }} />
                  <Bar dataKey="rain_mm" fill="#0ea5e9" opacity={0.7} name="Rain (mm)" />
                  <Bar dataKey="wind_kmh" fill="#f59e0b" opacity={0.5} name="Wind (km/h)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {(data.impacted || []).length > 0 && (
            <div className={`${subPanel} overflow-hidden`}>
              <div className={`px-3 py-2 text-[10px] uppercase tracking-wider ${mutedTxt} border-b border-slate-200 dark:border-slate-700/40`}>
                Weather-Impacted Activities (up to 100)
              </div>
              <div className="max-h-[380px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 dark:bg-slate-800/40 sticky top-0"><tr>
                    <th className={`p-2 text-left ${mutedTxt}`}>WBS</th>
                    <th className={`p-2 text-left ${mutedTxt}`}>Task</th>
                    <th className={`p-2 text-left ${mutedTxt}`}>Type</th>
                    <th className={`p-2 text-left ${mutedTxt}`}>Start</th>
                    <th className={`p-2 text-right ${mutedTxt}`}>Bad Days</th>
                    <th className={`p-2 text-right ${mutedTxt}`}>Suggested Delay</th>
                  </tr></thead>
                  <tbody>
                    {data.impacted.map(t => (
                      <tr key={t.task_id} className="border-t border-slate-200 dark:border-slate-700/30">
                        <td className={`p-2 ${mutedTxt} font-mono text-[10px]`}>{t.wbs_code}</td>
                        <td className={`p-2 ${headTxt}`}>{t.name}</td>
                        <td className={`p-2`}><span className={`text-[10px] px-2 py-0.5 rounded-full bg-sky-100 dark:bg-sky-500/20 text-sky-700 dark:text-sky-300`}>{t.activity_type}</span></td>
                        <td className={`p-2 ${bodyTxt}`}>{t.start?.slice(0,10)}</td>
                        <td className={`p-2 text-right ${bodyTxt}`}>{t.days_flagged}</td>
                        <td className={`p-2 text-right text-amber-600 dark:text-amber-400 font-semibold`}>+{t.recommended_finish_adjustment_days}d</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// TOP-LEVEL WRAPPER
// ═══════════════════════════════════════════════════
export default function SchedulePredictOps({ scheduleId }) {
  const [tab, setTab] = useState('mc');

  return (
    <div className="space-y-4">
      <div>
        <h3 className={`text-lg font-semibold ${headTxt}`}>AI Ops — Predict & Optimize</h3>
        <p className={`text-xs ${mutedTxt}`}>Monte Carlo · Predictive Delays · NL Query · Quality Index · Weather</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 dark:bg-[#0c1222] border border-slate-200 dark:border-slate-700/50 p-0.5 flex-wrap h-auto">
          <TabsTrigger value="mc"      className="text-xs px-3 py-1.5" data-testid="ai-tab-mc"><Dice5 className="w-3.5 h-3.5 mr-1.5" /> Monte Carlo</TabsTrigger>
          <TabsTrigger value="predict" className="text-xs px-3 py-1.5" data-testid="ai-tab-predict"><AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Predict Delays</TabsTrigger>
          <TabsTrigger value="nl"      className="text-xs px-3 py-1.5" data-testid="ai-tab-nl"><MessageCircle className="w-3.5 h-3.5 mr-1.5" /> NL Query</TabsTrigger>
          <TabsTrigger value="sqi"     className="text-xs px-3 py-1.5" data-testid="ai-tab-sqi"><ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Quality Index</TabsTrigger>
          <TabsTrigger value="weather" className="text-xs px-3 py-1.5" data-testid="ai-tab-weather"><CloudRain className="w-3.5 h-3.5 mr-1.5" /> Weather</TabsTrigger>
        </TabsList>

        <TabsContent value="mc"      className="mt-3 data-[state=inactive]:hidden" forceMount><MonteCarloPanel   scheduleId={scheduleId} /></TabsContent>
        <TabsContent value="predict" className="mt-3 data-[state=inactive]:hidden" forceMount><PredictDelaysPanel scheduleId={scheduleId} /></TabsContent>
        <TabsContent value="nl"      className="mt-3 data-[state=inactive]:hidden" forceMount><NLQueryPanel      scheduleId={scheduleId} /></TabsContent>
        <TabsContent value="sqi"     className="mt-3 data-[state=inactive]:hidden" forceMount><QualityIndexPanel scheduleId={scheduleId} /></TabsContent>
        <TabsContent value="weather" className="mt-3 data-[state=inactive]:hidden" forceMount><WeatherImpactPanel scheduleId={scheduleId} /></TabsContent>
      </Tabs>
    </div>
  );
}
